import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Trash, Spinner, Buildings, Globe } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/clients`);
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/clients`, { name, domain });
      setClients([...clients, res.data]);
      setDialogOpen(false);
      setName('');
      setDomain('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, clientName) => {
    if (!window.confirm(`Remover o cliente "${clientName}"? Usuarios e cursos vinculados serao desvinculados.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/clients/${id}`);
      setClients(clients.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error deleting client:', err);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-clients">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Clientes</h1>
            <p className="text-[#5C6B7A]">Cadastre as empresas que contratam a Tecnoservice</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setName(''); setDomain(''); setError(''); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#27AE60] hover:bg-[#219653]" data-testid="new-client-btn">
                <Plus size={18} className="mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
              <div className="relative px-8 pt-7 pb-5">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#27AE60] to-[#2C3E50]" />
                <DialogHeader>
                  <DialogTitle className="text-[#1A252F] text-xl font-bold">Novo Cliente</DialogTitle>
                  <p className="text-[#5C6B7A] text-sm mt-1">Cadastre uma nova empresa</p>
                </DialogHeader>
              </div>
              <form onSubmit={handleCreate} className="px-8 py-6 space-y-5" data-testid="client-form">
                {error && (
                  <div className="p-3 rounded-lg bg-[#E74C3C]/10 border border-[#E74C3C]/20">
                    <p className="text-sm text-[#E74C3C]">{error}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-[#1A252F] font-semibold text-sm">Nome da Empresa <span className="text-[#E74C3C]">*</span></Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Prosegur"
                    required
                    className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A252F] font-semibold text-sm">Dominio de E-mail</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-[#95A5A6] text-sm">@</span>
                    <Input
                      value={domain}
                      onChange={(e) => setDomain(e.target.value.toLowerCase().replace(/^@/, ''))}
                      placeholder="prosegur.com.br"
                      className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                      data-testid="client-domain-input"
                    />
                  </div>
                  <p className="text-xs text-[#5C6B7A]">Usuarios com este dominio serao vinculados automaticamente a este cliente ao se cadastrar</p>
                </div>
                <div className="flex gap-3 pt-2 pb-2">
                  <Button type="button" variant="outline" className="flex-1 h-11 border-[#BDC3C7] text-[#5C6B7A]" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creating} className="flex-1 h-11 bg-[#27AE60] hover:bg-[#219653] text-white font-medium" data-testid="save-client-btn">
                    {creating ? <Spinner size={18} className="animate-spin" /> : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-3">
          {clients.map((client) => (
            <div key={client.id} className="bg-white rounded-xl border border-[#2C3E50]/10 p-4 flex items-center gap-4" data-testid={`client-row-${client.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#2C3E50] flex items-center justify-center flex-shrink-0">
                <Buildings size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1A252F]">{client.name}</p>
                <div className="flex items-center gap-3 text-sm text-[#5C6B7A]">
                  {client.domain && (
                    <span className="flex items-center gap-1">
                      <Globe size={14} />
                      @{client.domain}
                    </span>
                  )}
                  <span>
                    {client.created_at ? new Date(client.created_at).toLocaleDateString('pt-BR') : '-'}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(client.id, client.name)} className="text-[#E74C3C] hover:text-[#E74C3C] hover:bg-[#E74C3C]/10" data-testid={`delete-client-${client.id}`}>
                <Trash size={18} />
              </Button>
            </div>
          ))}

          {clients.length === 0 && (
            <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-12 text-center text-[#5C6B7A]">
              Nenhum cliente cadastrado. Adicione o primeiro!
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminClients;
